import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ApiKeyScreen from '../components/onboarding/v2/ApiKeyScreen'
import ConfirmProfileScreen from '../components/onboarding/v2/ConfirmProfileScreen'
import ExtractionWaitScreen from '../components/onboarding/v2/ExtractionWaitScreen'
import OnboardingLayout from '../components/onboarding/v2/OnboardingLayout'
import ReviewScreen from '../components/onboarding/v2/ReviewScreen'
import UploadScreen from '../components/onboarding/v2/UploadScreen'
import WelcomeScreen from '../components/onboarding/v2/WelcomeScreen'
import { useOnboardingV2Store } from '../stores/onboardingV2Store'

type UploadSubStep = 'welcome' | 'api_key' | 'upload'

export default function OnboardingV2Page() {
  const { state, loadState, error, fetchState } = useOnboardingV2Store()
  const navigate = useNavigate()
  // Welcome, API key (BYO), and Upload all live inside ready_for_upload.
  const [uploadSubStep, setUploadSubStep] = useState<UploadSubStep>('welcome')

  useEffect(() => {
    void fetchState()
  }, [fetchState])

  // Onboarding ends at the library — there is no completion screen
  // (ONBOARDING_UX_SPEC.md: "Done screen | Eliminated"). On commit the user goes
  // straight to /library (success banner shown there). We must NOT keep polling
  // /v2/state once terminal: a completed session would spawn a fresh
  // ready_for_upload session on the next fetch.
  const isTerminal =
    state?.orchestration_state === 'committed' || state?.orchestration_state === 'complete'
  useEffect(() => {
    if (isTerminal) navigate('/library', { replace: true })
  }, [isTerminal, navigate])

  // Redirecting to /library once terminal.
  if (isTerminal) {
    return <FullScreenSpinner />
  }

  // Initial load only. Once state is loaded, transient loadState changes from
  // 2s polling (Extraction Wait) must not blank the current screen.
  if (!state) {
    if (loadState === 'error') {
      return (
        <OnboardingLayout>
          <div className="mx-auto max-w-[600px] text-center">
            <p className="font-body text-ob-error">{error ?? 'Something went wrong.'}</p>
            <button
              type="button"
              onClick={() => void fetchState()}
              className="mt-4 rounded-lg bg-ob-ink px-5 py-2.5 font-body text-sm font-medium text-ob-surface transition hover:opacity-90"
            >
              Try again
            </button>
          </div>
        </OnboardingLayout>
      )
    }
    return <FullScreenSpinner />
  }

  switch (state.orchestration_state) {
    case 'ready_for_upload':
      if (uploadSubStep === 'welcome') {
        return (
          <WelcomeScreen
            onGetStarted={() => {
              const needsKey = state.capability_mode === 'byo' && !state.has_valid_api_key
              setUploadSubStep(needsKey ? 'api_key' : 'upload')
            }}
          />
        )
      }
      if (uploadSubStep === 'api_key') {
        return <ApiKeyScreen onContinue={() => setUploadSubStep('upload')} />
      }
      return <UploadScreen />
    case 'ready_for_profile_confirm':
      return <ConfirmProfileScreen />
    case 'extraction_wait':
      return <ExtractionWaitScreen />
    case 'ready_for_review':
      return <ReviewScreen />
    default:
      return <StubScreen title="Onboarding" note={`State: ${state.orchestration_state}`} />
  }
}

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ob-surface">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-ob-secondary/30 border-t-ob-ink" />
    </div>
  )
}

function StubScreen({ title, note }: { title: string; note: string }) {
  return (
    <OnboardingLayout>
      <div className="mx-auto max-w-[600px] text-center">
        <h1 className="mb-3 font-serif text-2xl font-semibold text-ob-ink md:text-3xl">{title}</h1>
        <p className="font-body text-ob-secondary">{note}</p>
      </div>
    </OnboardingLayout>
  )
}
